import React, {Component} from 'react';
import toastr from 'toastr';
import produce from 'immer';
import withLifecycles from 'async-lifecycles';
import {Button, Header, Icon, Input, Item, Pagination, Popup, Table} from 'semantic-ui-react';

import {debounce} from 'lodash';
import usersAccessApi from '../../store/services/userAccess';
import RequiresRole, {ROLES} from '../security/RequiresRole';
import FormContainer from '../form-components/FormContainer';
import usersImage from '../assets/users.svg';
import CustomerAssociationPopup from './CustomerAssociationPopup';
import styled from 'react-emotion';
import {DateFormat} from '../components/date';

const Styles = styled.div`
  .tableHeader {
    background-color: '#f00';
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .ui.header {
      margin-bottom: 0;
    }
  }
`;

class RequestsTable extends Component {
  state = {
    loadingButton: false,
    loading: false,
    pageInfo: {},
    searchTerm: '',
    sortByCompany: false,
  };

  componentDidMount() {
    this._loadUsers();
  }

  _denyPendingUser = async user => {
    this.setState({loadingButton: true});

    var data = await usersAccessApi.denyPending({
      userId: user.id,
    });

    if (data && !data.hasErrors) {
      toastr.success('User Rejected');
    } else {
      toastr.error('Unable to Reject User');
    }
    this._loadUsers(false);
    this.setState({searchTerm: ''});
    this.props.onUpdated();
    this.setState({loadingButton: false});
  };

  _approveUser = async user => {
    this.setState({loadingButton: true});

    var data = await usersAccessApi.approvePending({
      userId: user.id,
      customerId: user.customerId,
    });

    if (data && !data.hasErrors) {
      toastr.success('User Approved');
    } else {
      toastr.error('Unable to Approve User');
    }

    this.setState({searchTerm: ''});
    this._loadUsers(0, false);
    this.props.onUpdated();
    this.setState({loadingButton: false});
  };

  _loadUsers = (pageNumber = 0, showLoading = true) => {
    withLifecycles(
      () =>
        usersAccessApi.fetchPending({
          request: {
            page: pageNumber,
            pageSize: 20,
          },
          searchTerm: this.state.searchTerm,
          sortByCompany: this.state.sortByCompany,
        }),
      ({loading, data, error}) => {
        this.setState(
          produce(draft => {
            if (showLoading) {
              draft.loading = loading;
            }

            if (data && !data.hasErrors) {
              draft.pageInfo = data.result;
            }
          }),
        );
      },
    );
  };

  render() {
    const {pageInfo} = this.state;
    const users = pageInfo.data || [];
    return (
      <FormContainer loading={this.state.loading}>
        <Styles>
          <div className="tableHeader">
            <Header as="h1">Requests</Header>
            <Input
              placeholder="Search Requests..."
              onChange={debounce((e, searchTerm) => {
                this.setState({searchTerm: searchTerm.value}, () =>
                  this._loadUsers(),
                );
              }, 400)}
            />
          </div>
        </Styles>
        {pageInfo.pageCount === 0 && (
          <Item.Group>
            <Item>
              <Item.Image size="mini" src={usersImage} />
              <Item.Content verticalAlign="middle">
                <strong>No Pending Requests</strong>
                <div>
                  <small>
                    New requests will appear here when a user requests to be
                    associated to a customer.
                  </small>
                </div>
              </Item.Content>
            </Item>
          </Item.Group>
        )}
        {pageInfo.pageCount > 0 && (
          <React.Fragment>
            <Table basic="very" selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell> </Table.HeaderCell>
                  <RequiresRole roles={[ROLES.SYSTEM_ADMIN, ROLES.MM_ADMIN]}>
                    <Table.HeaderCell
                      style={{cursor: 'pointer'}}
                      onClick={() => {
                        this.setState({sortByCompany: false}, () =>
                          this._loadUsers(0, false),
                        );
                      }}
                    >
                      Full Name {!this.state.sortByCompany && '▼'}
                    </Table.HeaderCell>
                  </RequiresRole>
                  <Table.HeaderCell
                    style={{cursor: 'pointer'}}
                    onClick={() =>
                      this.setState({sortByCompany: true}, () => {
                        this._loadUsers(0, false);
                      })
                    }
                  >
                    Company {this.state.sortByCompany && '▼'}
                  </Table.HeaderCell>
                  <Table.HeaderCell>Title</Table.HeaderCell>
                  <Table.HeaderCell>Email Address</Table.HeaderCell>
                  <Table.HeaderCell>Phone Number</Table.HeaderCell>
                  <Table.HeaderCell>Access</Table.HeaderCell>
                  <Table.HeaderCell>Requested Access</Table.HeaderCell>
                  <Table.HeaderCell>Request Date</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>{users.map(this._renderRow)}</Table.Body>
            </Table>

            <Pagination
              defaultActivePage={1}
              onPageChange={async (e, paginationInfo) => {
                await this._loadUsers(paginationInfo.activePage - 1);
              }}
              ellipsisItem={{
                content: <Icon name="ellipsis horizontal" />,
                icon: true,
              }}
              firstItem={{
                content: <Icon name="angle double left" />,
                icon: true,
              }}
              lastItem={{
                content: <Icon name="angle double right" />,
                icon: true,
              }}
              prevItem={{content: <Icon name="angle left" />, icon: true}}
              nextItem={{
                content: <Icon name="angle right" />,
                icon: true,
              }}
              totalPages={pageInfo.pageCount}
            />
          </React.Fragment>
        )}
      </FormContainer>
    );
  }

  _renderRow = user => (
    <Table.Row key={user.id}>
      <Table.Cell collapsing>
        <Popup
          trigger={
            <Button icon basic>
              <Icon name="thumbs outline up" />
            </Button>
          }
          header={
            user.requestedAccess === 'Customer User'
              ? 'Associate Company'
              : 'Approve Access'
          }
          content={
            user.requestedAccess === 'Customer User' ? (
              <CustomerAssociationPopup
                userIsDomainRestricted={user.isDomainRestricted}
                userId={user.id}
                onUpdated={() => {
                  this._loadUsers(0, false);
                  this.setState({searchTerm: ''});
                  this.props.onUpdated();
                }}
              />
            ) : (
              <Button
                icon
                positive
                onClick={() => {
                  this._approveUser(user);
                }}
                loading={this.state.loadingButton}
                fluid
                style={{marginTop: 10}}
              >
                Approve?
              </Button>
            )
          }
          on="click"
        />
        <Popup
          trigger={
            <Button icon basic>
              <Icon name="thumbs outline down" />
            </Button>
          }
          header={
            !user.isDomainRestricted ||
            (user.isDomainRestricted &&
              user.requestedAccess === 'Customer User')
              ? 'Reject Associate Request'
              : 'Reject Blacklist Bypass'
          }
          content={
            !user.isDomainRestricted ||
            (user.isDomainRestricted &&
              user.requestedAccess === 'Customer User') ? (
                <Button
                  icon
                  negative
                  onClick={() => this._denyPendingUser(user)}
                  loading={this.state.loadingButton}
                  fluid
                  style={{marginTop: 10}}
                >
                Are you sure?
                </Button>
              ) : (
                <Button
                  icon
                  negative
                  onClick={() => {
                    this._denyPendingUser(user);
                  }}
                  loading={this.state.loadingButton}
                  fluid
                  style={{marginTop: 10}}
                >
                Are you sure?
                </Button>
              )
          }
          on="click"
        />
      </Table.Cell>
      <Table.Cell>{user.fullName}</Table.Cell>
      <Table.Cell>{user.company}</Table.Cell>
      <Table.Cell>{user.title}</Table.Cell>
      <Table.Cell>
        {user.emailAddress}{' '}
        {user.isDomainRestricted ? '(Blacklisted Domain)' : null}
      </Table.Cell>
      <Table.Cell>{user.phoneNumber}</Table.Cell>
      <Table.Cell>{user.role}</Table.Cell>
      <Table.Cell>{user.requestedAccess}</Table.Cell>
      <Table.Cell><DateFormat date={user.createdDate} /></Table.Cell>
    </Table.Row>
  );
}

export default RequestsTable;
